import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_ITEMS, COMPANY } from "@/lib/content";

const TopBar = () => (
  <div className="hidden md:block bg-abayle-green-dark text-white text-xs relative" data-testid="top-bar">
    <div className="container-abayle flex items-center justify-between py-2">
      <div className="flex items-center gap-6 text-white/90">
        {COMPANY.phones.map((p) => (
          <span key={p} className="inline-flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 text-abayle-gold" />
            {p}
          </span>
        ))}
        <span className="text-white/80">{COMPANY.email}</span>
      </div>
      <div className="text-abayle-gold font-semibold tracking-widest uppercase text-[10px]">
        {COMPANY.tagline}
      </div>
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-px bg-abayle-gold/70" />
  </div>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <TopBar />
      <div
        className={`bg-white transition-all duration-300 ${
          scrolled ? "shadow-[0_4px_20px_-10px_rgba(15,74,58,0.25)]" : "shadow-none"
        }`}
      >
        <div className="container-abayle flex items-center justify-between py-3">
          <Link to="/" data-testid="logo-link" aria-label="Abayle home">
            <Logo size="lg" />
          </Link>
          <nav className="hidden lg:flex items-center gap-7 xl:gap-8" data-testid="primary-nav">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:block">
            <Link to="/contact" className="btn-primary-abayle whitespace-nowrap" data-testid="nav-cta-contact">
              Contact Us
            </Link>
          </div>

          <button
            className="lg:hidden inline-flex items-center justify-center p-2 rounded-md border border-abayle-gray-border text-abayle-green"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Gold divider below nav */}
        <div className="h-px bg-abayle-gold/25" />

        {open && (
          <div className="lg:hidden bg-white border-t border-abayle-gray-border" data-testid="mobile-menu">
            <div className="container-abayle py-4 flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `block py-3 px-2 text-sm font-semibold uppercase tracking-wide border-b border-abayle-gray-border ${
                      isActive ? "text-abayle-gold" : "text-abayle-green"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="btn-primary-abayle mt-4 w-full"
                data-testid="mobile-cta-contact"
              >
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
